import {
  computed,
  provide,
  type ComputedRef,
  shallowRef,
} from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionValuesRecord,
  type InjectionKeysRecord,
} from "@mcwd/util-vue";

import { type Nil, isNil, isString } from '@mcwd/typescript-type-guards';
import { useFormLogger } from "./use-form-logger.js";

type UseKnownVisitorInjectionValues = InjectionValuesRecord<{
  isMarketoKnownVisitor: ComputedRef<boolean | Nil>;
  initMarketoFormElement: (formElement: ComputedRef<Nil | JQuery<HTMLFormElement>>) => void; 
}>;

type UseKnownVisitorInjectionKeys = InjectionKeysRecord<UseKnownVisitorInjectionValues>;

const UseKnownVisitorInjectionKeys = CreateInjectionKeysRecord<UseKnownVisitorInjectionKeys>({
  isMarketoKnownVisitor: "isMarketoKnownVisitor",
  initMarketoFormElement: "initMarketoFormElement",
});

export function initMarketoKnownVisitorState()
{
  // To avoid circular dependencies, we provide the FormElement value later.
  const FormElementWrapper = shallowRef<Nil | ComputedRef<JQuery<HTMLFormElement> | Nil>>(null);
  const FormElement = computed(() => {
    if (isNil(FormElementWrapper.value)) {
      return null;
    }
    return FormElementWrapper.value.value;
  });
  if (checkValuesAlreadyProvided(UseKnownVisitorInjectionKeys)) {
    return;
  }

  const initMarketoFormElement = (formElement: ComputedRef<JQuery<HTMLFormElement> | Nil>) => {
    FormElementWrapper.value = formElement;
  };

  const isMarketoKnownVisitor = computed<boolean | Nil>(() => {
    if (isNil(FormElement.value)) {
      return null;
    }
    // In marketo, a known visitor is someone who has submitted a form before.
    // Marketo gives the option to show "Custom HTML" to known visitors.
    // This custom html is often called "Known Visitor Html" or KV HTML in the Marketo community.
    // This is a hidden input field we've set in the KV HTML to indicate that we know the current visitor
    const isKnownViewInput = FormElement.value.find<HTMLInputElement>(
        "input[name='isKnownView']"
      );
    let val =
      (isKnownViewInput.length === 1 ? isKnownViewInput.val() : null) ?? null;
    val = isString(val) ? val.trim() : val;
    return val === "true";
  });

  provide(UseKnownVisitorInjectionKeys.isMarketoKnownVisitor, isMarketoKnownVisitor);
  provide(UseKnownVisitorInjectionKeys.initMarketoFormElement, initMarketoFormElement);
}

export const useMarketoKnownVisitorState = ({ callerLabel }: { callerLabel?: string | undefined } = {}) => {
  const { logger } = useFormLogger();
  try {
    const isMarketoKnownVisitor = injectRequiredWithSelf(UseKnownVisitorInjectionKeys.isMarketoKnownVisitor);
    const initMarketoFormElement = injectRequiredWithSelf(UseKnownVisitorInjectionKeys.initMarketoFormElement);
    return { isMarketoKnownVisitor, initMarketoFormElement };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
};

